import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import { ITask, ITaskParameter } from './fi-task-shared';
import { FiTaskHelperService } from './fi-task-helper.service';
import { FiSignalRHelperService, IUpdateItemsSyncResult } from '@fi-shared/fi-signal-r/fi-signal-r-helper.service';
import { AccessRightType, ApplicationSettingsType, ObjectTypes, SideListOptionType } from '@fi-shared/types';
import { FiTaskSharedService } from '@fi-shared/fi-task-shared/fi-task-shared.service';
import { ITaskV2, ITaskV2Base } from '@fi-app/task-v2/fi-task-v2';
import { FiTaskV2HelperService } from '@fi-app/task-v2/fi-task-v2-helper.service';
import { FiTaskV2Service } from '@fi-app/task-v2/fi-task-v2.service';
import { AccessToApplicationTypeService } from '@fi-shared/fi-shared-services/access-to-application-type.service';

@Injectable()
export class FiTaskSyncService {
    constructor(
        private fiTaskService: FiTaskSharedService,
        private fiTaskHelperService: FiTaskHelperService,
        private fiSignalRHelperService: FiSignalRHelperService,
        private fiTaskV2HelperService: FiTaskV2HelperService,
        private accessToApplicationTypeService: AccessToApplicationTypeService,
    ) {
        this.fiSignalRHelperService.initialTaskIdsToBeDone$.subscribe((taskIds) => {
            this._taskIdsToBeDone$.next(taskIds);
        });
        this.fiTaskService.TasksUpdatedInternal$.subscribe((tasks) => this.updateTasksToBeDone(tasks));
    }

    private _taskIdsToBeDone$ = new BehaviorSubject<number[]>([]);
    get taskIdsToBeDone$(): Observable<number[]> {
        return this._taskIdsToBeDone$.asObservable();
    }
    get tasksToBeDoneCount$(): Observable<number> {
        return this.taskIdsToBeDone$.pipe(map((tasks) => tasks.length));
    }

    private updateTasksToBeDone(tasks: ITask[]) {
        const taskIdsToBeDone = this._taskIdsToBeDone$.value;
        let tasksToBeDoneChanged = false;
        tasks.forEach((task) => {
            const isTaskToBeDone = this.fiTaskHelperService.isTaskToBeDone(task);
            if (isTaskToBeDone && !taskIdsToBeDone.find((id) => id === task.Id)) {
                // add task to toBeDone items
                taskIdsToBeDone.push(task.Id);
                tasksToBeDoneChanged = true;
            } else if (!isTaskToBeDone && taskIdsToBeDone.find((id) => id === task.Id)) {
                // remove task from toBeDone items
                taskIdsToBeDone.splice(
                    taskIdsToBeDone.findIndex((id) => id === task.Id),
                    1,
                );
                tasksToBeDoneChanged = true;
            }
        });
        if (tasksToBeDoneChanged) {
            this._taskIdsToBeDone$.next(taskIdsToBeDone);
        }
    }

    updateTasksV2ToBeDone(tasks: ITaskV2Base[]) {
        const taskIdsToBeDone = this._taskIdsToBeDone$.value;
        let tasksToBeDoneChanged = false;
        tasks.forEach((task) => {
            const isTaskToBeDone = this.fiTaskV2HelperService.isTaskToBeDone(task);
            if (isTaskToBeDone && !taskIdsToBeDone.find((id) => id === task.Id)) {
                // add task to toBeDone items
                taskIdsToBeDone.push(task.Id);
                tasksToBeDoneChanged = true;
            } else if (!isTaskToBeDone && taskIdsToBeDone.find((id) => id === task.Id)) {
                // remove task from toBeDone items
                taskIdsToBeDone.splice(
                    taskIdsToBeDone.findIndex((id) => id === task.Id),
                    1,
                );
                tasksToBeDoneChanged = true;
            }
        });
        if (tasksToBeDoneChanged) {
            this._taskIdsToBeDone$.next(taskIdsToBeDone);
        }
    }

    updateTaskProps(currentTask: ITaskV2, updatedTask: ITaskV2) {
        currentTask.Status = updatedTask.Status;
        currentTask.Priority = updatedTask.Priority;
        currentTask.IsDeadlinePassed = updatedTask.IsDeadlinePassed;
        currentTask.Deadline = updatedTask.Deadline;
        currentTask.IsArchived = updatedTask.IsArchived;
    }

    subscribeToTaskUpdatedBySync(taskId: number, onDestroy$: Observable<void>, callback: (task: ITaskV2) => void) {
        this._updatedTasksSyncResult$.pipe(takeUntil(onDestroy$)).subscribe((syncResult) => {
            const updatedTask = syncResult.updatedItems.find((item) => item.Id === taskId);
            if (updatedTask) {
                callback(updatedTask);
            }
        });
    }
    private _updatedTasksSyncResult$ = new Subject<IUpdateItemsSyncResult<ITaskV2>>();
    get updatedTasksSyncResult$(): Observable<IUpdateItemsSyncResult<ITaskV2>> {
        return this._updatedTasksSyncResult$.asObservable();
    }
    initForSystemNotifications(fiTaskV2Service: FiTaskV2Service) {
        this.fiSignalRHelperService.subscribeToItemUpdated({
            objectType: ObjectTypes.Task,
            fetchItemsFn: (taskIds) => {
                const parameter: ITaskParameter = this.fiTaskV2HelperService.getUpdatedTaskParameter(
                    {},
                    {
                        fetchOnlyItems: true,
                    },
                    {
                        filterType: SideListOptionType.All,
                        ids: taskIds,
                        isArchived: undefined, // we should fetch both archived and not archived tasks
                    },
                );
                const promise = new Promise<ITaskV2[]>((resolve, reject) => {
                    if (
                        this.accessToApplicationTypeService.HasAccessToApplicationType(
                            ApplicationSettingsType.TaskV2Local,
                            AccessRightType.Read,
                        )
                    ) {
                        fiTaskV2Service.getTasksListData(parameter).then((result) => resolve(result.Items), reject);
                    } else {
                        this.fiTaskService
                            .getTasksListData(parameter)
                            .then((result) => resolve(<any>result.Items), reject);
                    }
                });
                return promise;
            },
            callback: (result: IUpdateItemsSyncResult<ITaskV2>) => {
                this._updatedTasksSyncResult$.next(result);
                this.updateTasksV2ToBeDone(result.updatedItems);

                const taskIdsToBeDone = this._taskIdsToBeDone$.value;
                let tasksToBeDoneChanged = false;

                result.deletedItemIds.forEach((deletedTaskId) => {
                    if (taskIdsToBeDone.find((id) => id === deletedTaskId)) {
                        // remove task from toBeDone items
                        taskIdsToBeDone.splice(
                            taskIdsToBeDone.findIndex((id) => id === deletedTaskId),
                            1,
                        );
                        tasksToBeDoneChanged = true;
                    }
                });

                if (tasksToBeDoneChanged) {
                    this._taskIdsToBeDone$.next(taskIdsToBeDone);
                }
            },
            debounceMs: 3000,
        });
    }
}
